import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  CheckSquare,
  X,
  Circle,
  CheckCircle2,
  AlertTriangle,
  Calendar,
  Clock,
  Phone,
  Video,
  CalendarCheck,
  MapPin,
  Mail,
  MessageSquare,
  CalendarClock,
  User,
  FileText,
  ArrowRight,
  Briefcase,
  FolderKanban,
  Search,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth';
import { getCurrentUserOrganizationId } from '../../lib/stores/referenceDataCache';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../hooks/useTheme';
import { TaskCompletionModal } from './TaskCompletionModal';
import { useSalesTargetStore } from '../../lib/stores/salesTargetStore';
import { ACTIVITY_GROUP_ORDER, compareByDueDate, formatActivityDueDate, groupActivitiesByDue } from '../../lib/utils/activitiesDrawerGroups';

// Activity type icons
const activityTypeIcons: Record<string, any> = {
  call: Phone,
  videochiamata: Video,
  appuntamento: CalendarCheck,
  sopralluogo: MapPin,
  email: Mail,
  task: CheckSquare,
  other: MessageSquare,
};

interface UnifiedActivity {
  id: string;
  title: string;
  due_date: string | null;
  type: string;
  source: 'commercial' | 'operative';
  client_name?: string;
  project_name?: string;
  priority?: string;
  status?: string;
}

type TabType = 'all' | 'commercial' | 'operative';

const GROUP_CONFIG = {
  overdue: { title: 'In ritardo', icon: AlertTriangle, iconClass: 'text-red-500' },
  today: { title: 'Oggi', icon: Clock, iconClass: 'text-blue-500' },
  tomorrow: { title: 'Domani', icon: Calendar, iconClass: 'text-amber-500' },
  this_week: { title: 'Questa settimana', icon: Calendar, iconClass: 'text-purple-500' },
  later: { title: 'Prossime', icon: Calendar, iconClass: 'opacity-60' },
  no_date: { title: 'Senza scadenza', icon: Calendar, iconClass: 'opacity-60' },
};

export function ActivitiesDrawer() {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [commercialActivities, setCommercialActivities] = useState<UnifiedActivity[]>([]);
  const [operativeActivities, setOperativeActivities] = useState<UnifiedActivity[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [completingActivity, setCompletingActivity] = useState<any>(null);
  const [isCompletionModalOpen, setIsCompletionModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const { fetchActivities: fetchSalesActivities } = useSalesTargetStore();

  const fetchPendingCount = useCallback(async () => {
    if (!user) return;
    try {
      const organizationId = await getCurrentUserOrganizationId();

      const [commercialRes, operativeRes] = await Promise.all([
        supabase
          .from('sales_activities')
          .select('id', { count: 'exact', head: true })
          .eq('organization_id', organizationId)
          .eq('assigned_to', user.id)
          // Aperta = né timestamp né status chiuso (i due campi possono divergere
          // sulle righe storiche: attività inserite già completate senza timestamp)
          .is('completed_at', null)
          .not('status', 'in', '(completed,cancelled)'),
        supabase
          .from('tasks')
          .select('id, project:projects!inner(id)', { count: 'exact', head: true })
          .eq('project.organization_id', organizationId)
          .eq('project.archived', false)
          .eq('assigned_to', user.id)
          .neq('status', 'completed')
          .eq('is_header', false),
      ]);

      const total = (commercialRes.count || 0) + (operativeRes.count || 0);
      setPendingCount(total);
    } catch (err) {
      console.error('Error fetching pending count:', err);
    }
  }, [user]);

  // Fetch count on mount and every 60s
  useEffect(() => {
    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, [fetchPendingCount]);

  const fetchActivities = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const organizationId = await getCurrentUserOrganizationId();

      const [commercialRes, operativeRes] = await Promise.all([
        supabase
          .from('sales_activities')
          .select(`
            id, title, due_date, type, status,
            client_id, clients:client_id (business_name),
            partner:external_partners!sales_activities_partner_id_fkey(name)
          `)
          .eq('organization_id', organizationId)
          .eq('assigned_to', user.id)
          .is('completed_at', null)
          .not('status', 'in', '(completed,cancelled)')
          .order('due_date', { ascending: true })
          .limit(100),
        supabase
          .from('tasks')
          .select(`
            id, title, due_date, status, priority,
            project:projects!inner(id, name, client:clients(id, business_name))
          `)
          .eq('project.organization_id', organizationId)
          .eq('project.archived', false)
          .eq('assigned_to', user.id)
          .neq('status', 'completed')
          .eq('is_header', false)
          .order('due_date', { ascending: true, nullsFirst: false })
          .limit(100),
      ]);

      const commercial: UnifiedActivity[] = (commercialRes.data || []).map((a: any) => ({
        id: a.id,
        title: a.title,
        due_date: a.due_date,
        type: a.type || 'task',
        source: 'commercial' as const,
        client_name: a.clients?.business_name ?? (a.partner?.name ? `Partner: ${a.partner.name}` : undefined),
        status: a.status,
      }));

      // Anche le task senza scadenza: il contatore dell'icona le conta (284ed7c2).
      const operative: UnifiedActivity[] = (operativeRes.data || [])
        .map((t: any) => ({
          id: t.id,
          title: t.title,
          due_date: t.due_date,
          type: 'task',
          source: 'operative' as const,
          project_name: t.project?.name,
          client_name: t.project?.client?.business_name,
          priority: t.priority,
          status: t.status,
        }));

      setCommercialActivities(commercial);
      setOperativeActivities(operative);
    } catch (err) {
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const openDrawer = () => {
    setIsOpen(true);
    setIsClosing(false);
    fetchActivities();
    document.body.style.overflow = 'hidden';
  };

  const closeDrawer = () => {
    setIsClosing(true);
    document.body.style.overflow = '';
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 200);
  };

  // Commercial activities: show outcome modal
  const handleComplete = (activity: UnifiedActivity) => {
    if (activity.source === 'commercial') {
      // Fetch full activity data for the modal
      fetchFullCommercialActivity(activity.id);
    } else {
      // Operative: complete directly
      completeOperativeTask(activity);
    }
  };

  const fetchFullCommercialActivity = async (activityId: string) => {
    try {
      const { data } = await supabase
        .from('sales_activities')
        .select(`
          id, title, description, type, status, due_date,
          client_id, deal_id, lead_id, assigned_to,
          clients:client_id (business_name),
          deals:deal_id (title, amount),
          partner:external_partners!sales_activities_partner_id_fkey(name)
        `)
        .eq('id', activityId)
        .single();

      if (data) {
        setCompletingActivity({
          ...data,
          client_name: data.clients?.business_name,
          partner_name: (data.partner as any)?.name,
          deal_title: (data.deals as any)?.title,
          deal_amount: (data.deals as any)?.amount,
        });
        setIsCompletionModalOpen(true);
      }
    } catch (err) {
      console.error('Error fetching activity for completion:', err);
    }
  };

  const submitActivityCompletion = async (activityId: string, outcome: string) => {
    try {
      const { error } = await supabase
        .from('sales_activities')
        .update({
          status: 'completed',
          outcome,
          completed_at: new Date().toISOString(),
        })
        .eq('id', activityId);

      if (error) throw error;

      setCommercialActivities((prev) => prev.filter((a) => a.id !== activityId));
      setPendingCount((prev) => Math.max(0, prev - 1));

      // Refresh sales activities in dashboard
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser?.id) {
        fetchSalesActivities(currentUser);
      }
    } catch (err) {
      console.error('Error completing activity:', err);
    }
  };

  const completeOperativeTask = async (activity: UnifiedActivity) => {
    try {
      await supabase
        .from('tasks')
        .update({ status: 'completed', completed_at: new Date().toISOString() })
        .eq('id', activity.id);
      setOperativeActivities((prev) => prev.filter((a) => a.id !== activity.id));
      setPendingCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error completing task:', err);
    }
  };

  const handleNavigate = (activity: UnifiedActivity) => {
    closeDrawer();
    if (activity.source === 'commercial') {
      navigate('/my-tasks');
    } else {
      navigate('/my-tasks');
    }
  };

  const displayedActivities = useMemo(() => {
    let activities: UnifiedActivity[];
    if (activeTab === 'commercial') activities = commercialActivities;
    else if (activeTab === 'operative') activities = operativeActivities;
    else activities = [...commercialActivities, ...operativeActivities].sort(compareByDueDate);

    if (typeFilter) {
      activities = activities.filter((a) => a.type === typeFilter);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      activities = activities.filter((a) =>
        a.title?.toLowerCase().includes(term) ||
        a.client_name?.toLowerCase().includes(term) ||
        a.project_name?.toLowerCase().includes(term)
      );
    }
    return groupActivitiesByDue(activities);
  }, [activeTab, commercialActivities, operativeActivities, searchTerm, typeFilter]);

  const filteredCount = useMemo(() =>
    Object.values(displayedActivities).reduce((acc, list) => acc + list.length, 0),
    [displayedActivities]
  );

  const groupOrder = ACTIVITY_GROUP_ORDER;

  return (
    <>
      {/* Trigger Button */}
      <button
        className="relative flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-accent text-foreground transition-colors"
        onClick={openDrawer}
        data-tooltip-id="header-tooltip"
        data-tooltip-content="Le mie attività"
      >
        <CheckSquare className="h-5 w-5 text-[#E91E8C]" />
        {pendingCount > 0 && (
          <span className="absolute top-0.5 right-0.5 flex items-center justify-center min-w-[14px] h-3.5 px-1 text-[10px] font-semibold text-white bg-fuchsia-500 rounded-full">
            {pendingCount > 99 ? '99+' : pendingCount}
          </span>
        )}
      </button>

      {/* Drawer via Portal */}
      {isOpen &&
        createPortal(
          <>
            {/* Overlay */}
            <div
              className={`fixed inset-0 z-[9996] bg-black/20 backdrop-blur-sm duration-300 ${
                isClosing ? 'animate-out fade-out-0' : 'animate-in fade-in-0'
              }`}
              onClick={closeDrawer}
            />

            {/* Drawer panel */}
            <div
              className={`fixed top-0 right-0 bottom-0 z-[9997] w-full max-w-3xl bg-background border-l border-border shadow-2xl duration-300 flex flex-col ${
                isClosing ? 'animate-out slide-out-to-right' : 'animate-in slide-in-from-right'
              }`}
            >
              {/* Header */}
              <div className="flex-shrink-0 border-b border-border">
                <div className="px-4 py-3 flex items-center justify-between">
                  <h2 className="text-base font-semibold">Le mie attività</h2>
                  <button
                    onClick={closeDrawer}
                    className="p-1.5 rounded-lg hover:bg-accent transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="px-4 pb-2 flex items-center gap-1">
                  {([
                    { key: 'all', label: 'Tutte', count: commercialActivities.length + operativeActivities.length },
                    { key: 'commercial', label: 'Commerciali', icon: Briefcase, count: commercialActivities.length },
                    { key: 'operative', label: 'Operative', icon: FolderKanban, count: operativeActivities.length },
                  ] as { key: TabType; label: string; icon?: any; count: number }[]).map((tab) => (
                    <button
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                        activeTab === tab.key
                          ? isLight
                            ? 'bg-[rgba(55,53,47,0.08)] text-[#37352F]'
                            : 'bg-white/10 text-white'
                          : isLight
                            ? 'text-[#787774] hover:bg-[rgba(55,53,47,0.04)]'
                            : 'text-gray-400 hover:bg-white/[0.04]'
                      }`}
                    >
                      {tab.icon && <tab.icon className="h-3 w-3" />}
                      {tab.label}
                      <span className="opacity-60">({tab.count})</span>
                    </button>
                  ))}
                </div>

                {/* Filters */}
                <div className="px-4 pb-3 flex items-center gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#787774]" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Cerca per titolo, cliente, progetto..."
                      className={`w-full pl-8 pr-2 py-1.5 text-xs rounded-md border outline-none transition-colors ${
                        isLight
                          ? 'bg-white border-[rgba(55,53,47,0.16)] text-[#37352F] placeholder:text-[#787774] focus:border-[rgba(55,53,47,0.3)]'
                          : 'bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-gray-500'
                      }`}
                    />
                  </div>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className={`px-2 py-1.5 text-xs rounded-md border outline-none transition-colors ${
                      isLight
                        ? 'bg-white border-[rgba(55,53,47,0.16)] text-[#37352F]'
                        : 'bg-gray-800 border-gray-700 text-white'
                    }`}
                  >
                    <option value="">Tutti i tipi</option>
                    <option value="task">Task</option>
                    <option value="call">Chiamata</option>
                    <option value="videochiamata">Videochiamata</option>
                    <option value="appuntamento">Appuntamento</option>
                    <option value="sopralluogo">Sopralluogo</option>
                    <option value="email">Email</option>
                    <option value="other">Altro</option>
                  </select>
                  {(searchTerm || typeFilter) && (
                    <button
                      onClick={() => { setSearchTerm(''); setTypeFilter(''); }}
                      className="flex items-center gap-1 px-2 py-1.5 text-xs rounded-md text-[#787774] hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/[0.04] transition-colors"
                      title="Pulisci filtri"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="p-6 space-y-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="animate-pulse flex items-start gap-3">
                        <div className="h-4 w-4 bg-muted rounded-full mt-0.5" />
                        <div className="flex-1">
                          <div className="h-4 bg-muted rounded w-3/4 mb-1" />
                          <div className="h-3 bg-muted rounded w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-2">
                    {groupOrder.map((groupKey) => {
                      const activities = displayedActivities[groupKey];
                      if (!activities || activities.length === 0) return null;

                      const config = GROUP_CONFIG[groupKey];
                      const GroupIcon = config.icon;

                      return (
                        <div key={groupKey} className="mb-1">
                          {/* Group header */}
                          <div className={`flex items-center gap-1.5 px-4 py-1.5 sticky top-0 z-10 ${
                            isLight ? 'bg-[#FBFBFA]' : 'bg-gray-900/95'
                          }`}>
                            <GroupIcon className={`h-3.5 w-3.5 ${config.iconClass}`} />
                            <span className="text-xs font-semibold uppercase tracking-wide opacity-70">
                              {config.title}
                            </span>
                            <span className="text-xs opacity-40">({activities.length})</span>
                          </div>

                          {/* Activity items */}
                          {activities.map((activity) => {
                            const TypeIcon = activityTypeIcons[activity.type] || MessageSquare;
                            const { text: dateText, isOverdue, isToday: isTodayDate } = formatActivityDueDate(activity.due_date);

                            return (
                              <div
                                key={`${activity.source}-${activity.id}`}
                                className={`flex items-start gap-2.5 px-4 py-2 cursor-pointer transition-colors ${
                                  isLight
                                    ? 'hover:bg-[rgba(55,53,47,0.04)]'
                                    : 'hover:bg-white/[0.04]'
                                }`}
                                onClick={() => handleNavigate(activity)}
                              >
                                {/* Checkbox */}
                                <button
                                  className="flex-shrink-0 mt-0.5"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleComplete(activity);
                                  }}
                                >
                                  <Circle className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors" />
                                </button>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                  {/* Title + date */}
                                  <div className="flex items-start justify-between gap-2">
                                    <p className="text-sm font-medium line-clamp-1 flex-1 min-w-0">
                                      {activity.title}
                                    </p>
                                    <span
                                      className={`flex-shrink-0 text-xs font-medium px-1.5 py-0.5 rounded ${
                                        isOverdue
                                          ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                                          : isTodayDate
                                            ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                                            : 'text-gray-500'
                                      }`}
                                    >
                                      {dateText}
                                    </span>
                                  </div>

                                  {/* Meta row */}
                                  <div className="flex items-center gap-1.5 mt-0.5">
                                    <TypeIcon className="h-3 w-3 text-[#787774]" />

                                    {/* Source badge */}
                                    <span className={`text-[10px] px-1 py-0.5 rounded ${
                                      activity.source === 'commercial'
                                        ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400'
                                        : 'bg-purple-50 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
                                    }`}>
                                      {activity.source === 'commercial' ? 'Comm.' : 'Oper.'}
                                    </span>

                                    {/* Client or Project */}
                                    {activity.client_name && (
                                      <span className="flex items-center gap-0.5 text-xs text-[#787774] truncate max-w-[120px]">
                                        <User className="h-2.5 w-2.5 flex-shrink-0" />
                                        {activity.client_name}
                                      </span>
                                    )}
                                    {activity.project_name && (
                                      <span className="flex items-center gap-0.5 text-xs text-[#787774] truncate max-w-[120px]">
                                        <FolderKanban className="h-2.5 w-2.5 flex-shrink-0" />
                                        {activity.project_name}
                                      </span>
                                    )}

                                    {/* Priority for operative tasks */}
                                    {activity.priority && activity.priority !== 'medium' && (
                                      <span className={`text-[10px] px-1 py-0.5 rounded font-medium ${
                                        activity.priority === 'urgent'
                                          ? 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                                          : activity.priority === 'high'
                                            ? 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400'
                                            : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                                      }`}>
                                        {activity.priority === 'urgent' ? 'Urgente' : activity.priority === 'high' ? 'Alta' : 'Bassa'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}

                    {/* Empty state */}
                    {commercialActivities.length === 0 && operativeActivities.length === 0 && !loading && (
                      <div className="flex flex-col items-center justify-center py-12">
                        <CheckSquare className="h-12 w-12 opacity-20 mb-3" />
                        <p className="text-sm opacity-50">Nessuna attività in sospeso</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="flex-shrink-0 border-t border-border px-4 py-3">
                <button
                  onClick={() => {
                    closeDrawer();
                    navigate('/my-tasks');
                  }}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors ${
                    isLight ? 'text-blue-600 hover:text-blue-700' : 'text-blue-400 hover:text-blue-300'
                  }`}
                >
                  Vedi tutte le attività
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </>,
          document.body
        )}
      {/* Outcome modal for commercial activities */}
      <TaskCompletionModal
        isOpen={isCompletionModalOpen}
        onClose={() => {
          setIsCompletionModalOpen(false);
          setCompletingActivity(null);
        }}
        onComplete={submitActivityCompletion}
        task={completingActivity}
      />
    </>
  );
}

export default ActivitiesDrawer;
